import { useSyncExternalStore } from "react";

/**
 * 다운로드 시작 요청을 트리거에서 진행 패널로 전달하는 신호.
 *
 * 트리거는 상세 헤더 액션과 Files 트리 폴더 액션 둘이다. 진행은 본문이 그린다. 그래서 둘은
 * 부모-자식이 아니라 형제다. 데이터셋별로 저장하므로 창을 여러 개 띄워도 서로 영향이 없다.
 *
 * **트리거가 디렉터리 핸들을 함께 보낸다.** `showDirectoryPicker()` 는 transient user activation 을
 * 요구한다. 그래서 클릭 핸들러에서 동기적으로 열어야 하고, 패널이 신호를 받은 뒤에는 열 수 없다.
 */

export type DatasetDownloadRequest = {
  // 사용자가 고른 폴더. 파일은 이 아래 `folderName` 폴더에 저장된다.
  directory: FileSystemDirectoryHandle;
  // 데이터셋을 담을 폴더 이름(서버가 준 `download_folder_name` 그대로). 고른 폴더에
  // 트리를 바로 저장하면 데이터셋 둘을 같은 폴더로 받았을 때 파일이 섞인다.
  folderName: string;
  // 폴더 스코프(`DirectoryFolder.path`). null 이면 데이터셋 전체.
  prefix: string | null;
  includeExcluded: boolean;
  // 같은 조건으로 다시 눌러도 새 실행으로 구분하는 일련번호.
  nonce: number;
};

const requests = new Map<string, DatasetDownloadRequest>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

export const datasetDownloadStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  request(datasetUuid: string): DatasetDownloadRequest | null {
    return requests.get(datasetUuid) ?? null;
  },
  start(datasetUuid: string, request: Omit<DatasetDownloadRequest, "nonce">): void {
    const nonce = (requests.get(datasetUuid)?.nonce ?? 0) + 1;
    requests.set(datasetUuid, { ...request, nonce });
    emit();
  },
  clear(datasetUuid: string): void {
    if (!requests.delete(datasetUuid)) return;
    emit();
  },
};

/** 이 데이터셋의 다운로드 시작 요청. 없으면 null. */
export function useDatasetDownloadRequest(datasetUuid: string): DatasetDownloadRequest | null {
  return useSyncExternalStore(datasetDownloadStore.subscribe, () =>
    datasetDownloadStore.request(datasetUuid),
  );
}
